// Diálogo de confirmación reutilizable en toda la app (reemplaza al
// window.confirm nativo). Se usa vía el hook useConfirm(), que devuelve
// una función `confirm()` que resuelve una Promise<boolean> cuando el
// usuario acepta o cancela.
import { useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, AlertTriangle } from "lucide-react";

function ConfirmDialog({ title, message, confirmLabel = "Eliminar", danger = true, onConfirm, onCancel }) {
  return ReactDOM.createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        onClick={onCancel}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          zIndex: 300,
        }}
      />

      {/* Contenedor centrador — no animado para que flexbox no se rompa */}
      <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 301,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        pointerEvents: "none",
      }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.82, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.88, y: 12 }}
        transition={{ type: "spring", stiffness: 460, damping: 32, mass: 0.55 }}
        style={{
          width: "100%",
          maxWidth: 360,
          pointerEvents: "all",
        }}
      >
        <div style={{
          background: "rgba(248,248,250,0.97)",
          backdropFilter: "blur(48px) saturate(200%)",
          WebkitBackdropFilter: "blur(48px) saturate(200%)",
          borderRadius: 22,
          border: "1px solid rgba(255,255,255,0.75)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.28), 0 0 0 0.5px rgba(0,0,0,0.07)",
          overflow: "hidden",
          pointerEvents: "all",
        }}>
          {/* Contenido */}
          <div style={{ padding: "32px 28px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 58, height: 58, borderRadius: 18,
              background: danger ? "rgba(229,62,62,0.1)" : "rgba(0,168,150,0.1)",
              border: `1.5px solid ${danger ? "rgba(229,62,62,0.2)" : "rgba(0,168,150,0.2)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {danger
                ? <Trash2 size={24} style={{ color: "#E53E3E" }} />
                : <AlertTriangle size={24} style={{ color: "var(--primary)" }} />
              }
            </div>

            <div>
              <h3 style={{
                fontSize: "1rem", fontWeight: 700,
                letterSpacing: "-0.3px", color: "var(--text)",
                marginBottom: 6,
              }}>
                {title}
              </h3>
              <p style={{
                fontSize: "0.82rem", color: "var(--text-muted)",
                lineHeight: 1.55, maxWidth: 280, margin: "0 auto",
              }}>
                {message}
              </p>
            </div>
          </div>

          {/* Botones estilo macOS: separados por líneas */}
          <div style={{ borderTop: "1px solid rgba(0,0,0,0.07)", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <ConfirmBtn onClick={onCancel} color="var(--text-muted)" border>
              Cancelar
            </ConfirmBtn>
            <ConfirmBtn onClick={onConfirm} color={danger ? "#E53E3E" : "var(--primary)"} bold>
              {confirmLabel}
            </ConfirmBtn>
          </div>
        </div>
      </motion.div>
      </div>
    </>,
    document.body
  );
}

function ConfirmBtn({ onClick, color, border, bold, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "15px 8px",
        background: hover ? "rgba(0,0,0,0.04)" : "transparent",
        border: "none",
        borderLeft: border ? "none" : "1px solid rgba(0,0,0,0.07)",
        color,
        fontFamily: "inherit",
        fontSize: "0.9rem",
        fontWeight: bold ? 700 : 500,
        cursor: "pointer",
        transition: "background 0.12s",
        letterSpacing: "-0.1px",
      }}
    >
      {children}
    </button>
  );
}

// Hook para usar el diálogo con await
export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback(({ title, message, confirmLabel, danger }) => {
    return new Promise((resolve) => {
      setState({ title, message, confirmLabel, danger, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState(prev => { prev?.resolve(true); return null; });
  }, []);

  const handleCancel = useCallback(() => {
    setState(prev => { prev?.resolve(false); return null; });
  }, []);

  const ConfirmUI = (
    <AnimatePresence>
      {state && (
        <ConfirmDialog
          key="confirm-dialog"
          title={state.title}
          message={state.message}
          confirmLabel={state.confirmLabel}
          danger={state.danger ?? true}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </AnimatePresence>
  );

  return { confirm, ConfirmUI };
}
